/* eslint-disable prettier/prettier */
export  const FILE_TYPE_MAP: Record<string, string[]> = {
    pdf: [".pdf"],

    document: [
        ".pdf",
        ".doc",
        ".docx",
        ".txt",
        ".rtf",
        ".odt",
    ],

    video: [
        ".mp4",
        ".mov",
        ".avi",
        ".mkv",
        ".wmv",
        ".flv",
        ".webm",
        ".mpeg",
    ],

    image: [
        ".jpg",
        ".jpeg",
        ".png",
        ".gif",
        ".bmp",
        ".svg",
        ".webp",
    ],

    audio: [
        ".mp3",
        ".wav",
        ".aac",
        ".ogg",
        ".m4a",
    ],

    spreadsheet: [
        ".xls",
        ".xlsx",
        ".csv",
    ],

    presentation: [
        ".ppt",
        ".pptx",
    ],
};

export const getFileTypeLabel = (allowedTypes: string[] = []) => {
    const types = allowedTypes.map(t => t.toLowerCase());

    if (types.includes(".pdf") && types.length === 1) return "PDF";

    if (
        types.some(t =>
            [".mp4", ".mov", ".avi", ".mkv", ".wmv", ".webm", ".flv"].includes(t)
        )
    )
        return "Video";

    if (
        types.some(t =>
            [".mp3", ".wav", ".aac", ".ogg", ".m4a"].includes(t)
        )
    )
        return "Audio";

    if (
        types.some(t =>
            [".doc", ".docx", ".txt", ".rtf", ".odt"].includes(t)
        )
    )
        return "Document";

    if (
        types.some(t =>
            [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".svg", ".webp"].includes(t)
        )
    )
        return "Image";

    if (
        types.some(t =>
            [".xls", ".xlsx", ".csv"].includes(t)
        )
    )
        return "Spreadsheet";

    if (
        types.some(t =>
            [".ppt", ".pptx"].includes(t)
        )
    )
        return "Presentation";

    return "--";
};

// "  .PDF " -> "pdf", "..docx" -> "docx"
export const normalizeExtension = (type?: string | null): string =>
    (type ?? '').trim().toLowerCase().replace(/^\.+/, '');

/**
 * Extension of a file name without the dot, lowercased.
 * "Report.Final.PDF" -> "pdf", "README" -> "", "archive." -> "", ".env" -> "env"
 */
export const getFileExtension = (fileName?: string | null): string => {
    const name = (fileName ?? '').trim();
    const lastDot = name.lastIndexOf('.');

    if (lastDot < 0 || lastDot === name.length - 1) return '';

    return normalizeExtension(name.slice(lastDot + 1));
};

const EXTENSION_CATEGORY_ORDER = [
    'Document',
    'PDF',
    'Image',
    'Video',
    'Audio',
    'Spreadsheet',
    'Presentation',
] as const;

const EXTENSION_CATEGORY_MAP: Record<string, string> = {
    pdf: 'PDF',
    doc: 'Document',
    docx: 'Document',
    txt: 'Document',
    rtf: 'Document',
    odt: 'Document',
    jpg: 'Image',
    jpeg: 'Image',
    png: 'Image',
    gif: 'Image',
    bmp: 'Image',
    svg: 'Image',
    webp: 'Image',
    mp4: 'Video',
    mov: 'Video',
    avi: 'Video',
    mkv: 'Video',
    wmv: 'Video',
    flv: 'Video',
    webm: 'Video',
    mpeg: 'Video',
    mp3: 'Audio',
    wav: 'Audio',
    aac: 'Audio',
    ogg: 'Audio',
    m4a: 'Audio',
    xls: 'Spreadsheet',
    xlsx: 'Spreadsheet',
    csv: 'Spreadsheet',
    ppt: 'Presentation',
    pptx: 'Presentation',
};

/**
 * Human-readable category for a list of allowed extensions.
 * [.png, .jpg] -> "Image", [.mp4, .mp3] -> "Video/Audio",
 * [.pdf] -> "PDF", [.pdf, .docx] -> "Document", [.zip] -> "ZIP", [] -> "-"
 */
export const getFileCategoryLabel = (allowedTypes: string[] = []): string => {
    const extensions = Array.from(
        new Set(allowedTypes.map(normalizeExtension).filter(Boolean))
    );

    if (extensions.length === 0) return '-';

    const categories = new Set<string>();
    const unknown: string[] = [];

    extensions.forEach(ext => {
        const category = EXTENSION_CATEGORY_MAP[ext];
        if (category) categories.add(category);
        else unknown.push(ext.toUpperCase());
    });

    // PDF alongside other document types is just a "Document"
    if (categories.has('Document')) categories.delete('PDF');

    const known = EXTENSION_CATEGORY_ORDER.filter(c => categories.has(c));

    return [...known, ...unknown].join('/');
};
